import {
  Divider,
  Flex,
  type FlexProps,
  NestedContentItem,
  ReportSection,
  ReportSectionItemContent,
  ReportSectionItemTable,
  Text,
} from '@/components';
import {
  BusinessFormLabelModifier,
  CorporateFormLabelModifier,
  DiscriminatedInspectionEvaluationModel,
  FundNecessityTypeLabelModifier,
  OperationFormLabelModifier,
} from '@/features/project/projects/_models/project';
import type { ProjectLedgerData } from '@/features/project/projects/detail/ledger/_hooks/useProjectLedgerData';
import { formatProjectStartYearForPdf } from '@/utils';
import { type FC, Fragment, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  LEDGER_BORDER,
  getLegalBaseSectionTableData,
  getPlanSectionTableData,
  getRelatedProjectNames,
} from '../../../utils';
import { LedgerTitle } from './LedgerTitle';

export const KsBasicInfoSection: FC<{
  ledgerData: ProjectLedgerData;
}> = ({
  ledgerData: {
    project,
    ministryName,
    organizationNameBySnapshot,
    businessManagerNameBySnapshot,
    otherOrganizationNameBySnapshot,
    data: {
      ks: { additionalJurisdictionalUsersBySnapshot },
    },
  },
}) => {
  const { t } = useTranslation();

  const legalBasesTableData = getLegalBaseSectionTableData({ project });
  const planTableData = getPlanSectionTableData({ project });

  const parsed = DiscriminatedInspectionEvaluationModel.safeParse(project);
  if (!parsed.success || parsed.data.sheetType !== 'KS') {
    return null;
  }
  const { inspectionEvaluation } = parsed.data;

  return (
    <>
      <Flex vertical gap={8}>
        <Flex gap={16}>
          <Text
            text={t('fund-sheet-report-title', { year: project?.fiscalYear })}
            variant="footnote"
          />
          <Text text={ministryName} variant="footnote" />
        </Flex>
        <LedgerTitle projectId={project?.id} projectName={project?.name} />
      </Flex>
      <Divider />
      <ReportSection
        title={t('basic-information')}
        items={[
          {
            subtitle: t('organization-information'),
            item: (
              <Flex vertical>
                <BasicInfoContentItem
                  label={t('ministry')}
                  value={ministryName}
                />
                <BasicInfoContentItem
                  label={t('organization-in-charge')}
                  value={organizationNameBySnapshot}
                />
                <BasicInfoContentItem
                  label={t('business-manager')}
                  value={businessManagerNameBySnapshot}
                />
                {additionalJurisdictionalUsersBySnapshot?.length
                  ? additionalJurisdictionalUsersBySnapshot.map((d, i) => (
                      // biome-ignore lint/suspicious/noArrayIndexKey: biomeをCIに仕込むために暫定的にignore。keyにユニークな値を指定するか適切な理由に書き換えること
                      <Fragment key={i}>
                        <BasicInfoContentItem
                          label={t('ledger-additional-ministry')}
                          value={d.ministryName}
                        />
                        <BasicInfoContentItem
                          label={t('ledger-additional-organization-in-charge')}
                          value={d.organizationName}
                        />
                        <BasicInfoContentItem
                          label={t('ledger-additional-business-manager')}
                          value={d.businessManagerNames}
                        />
                      </Fragment>
                    ))
                  : null}

                <BasicInfoContentItem
                  label={t('other-organization-in-charge')}
                  value={otherOrganizationNameBySnapshot}
                  isLast
                />
              </Flex>
            ),
          },
          {
            subtitle: t('basic-information'),
            item: (
              <Flex vertical>
                <Flex>
                  <BasicInfoContentItem
                    label={t('project-number')}
                    value={project?.projectNumber}
                  />
                  <BasicInfoContentItem
                    label={t('ledger-fund-sheet-number')}
                    value={project?.fundAdditionalAttributes?.fundSheetNumber}
                    wrapperStyle={{ flex: 0.75, borderLeft: LEDGER_BORDER }}
                  />
                  <BasicInfoContentItem
                    label={t('ledger-fund-sheet-branch-number')}
                    value={
                      project?.fundAdditionalAttributes?.fundSheetBranchNumber
                    }
                    wrapperStyle={{ flex: 0.75, borderLeft: LEDGER_BORDER }}
                  />
                </Flex>
                <BasicInfoContentItem
                  label={t('fund-business-name')}
                  value={project?.fundAdditionalAttributes?.fundBusinessName}
                />
                <NestedContentItem
                  mainRowData={[
                    {
                      label: t('fund-owner'),
                      value: project?.fundAdditionalAttributes?.fundOwner,
                    },
                    {
                      label: t('corporate-form'),
                      value: project?.fundAdditionalAttributes?.corporateForm
                        ? project.fundAdditionalAttributes.corporateForm ===
                          'others'
                          ? project.fundAdditionalAttributes
                              .optionalCorporateForm
                          : CorporateFormLabelModifier[
                              project.fundAdditionalAttributes.corporateForm
                            ]
                        : null,
                    },
                  ]}
                  subRowsData={[
                    {
                      label: t(
                        'eligibility-inspection-method-and-reasons-for-selection',
                      ),
                      value:
                        inspectionEvaluation.eligibilityInspection
                          ?.methodAndReasonsForSelection,
                    },
                    {
                      label: t('eligibility-inspection-results'),
                      value:
                        inspectionEvaluation.eligibilityInspection?.results,
                    },
                    {
                      label: t('implementation-status-of-inspection'),
                      value:
                        inspectionEvaluation.implementationStatusOfInspection,
                    },
                  ]}
                />

                <Flex>
                  <BasicInfoContentItem
                    label={t('operation-form')}
                    value={project?.fundAdditionalAttributes?.operationForms
                      ?.map((form) => OperationFormLabelModifier[form])
                      .join(', ')}
                    isLast
                  />
                  <BasicInfoContentItem
                    label={t('business-form')}
                    value={project?.fundAdditionalAttributes?.businessForms
                      ?.map((form) => BusinessFormLabelModifier[form])
                      .join(', ')}
                    wrapperStyle={{ borderLeft: LEDGER_BORDER }}
                    isLast
                  />
                </Flex>
              </Flex>
            ),
          },

          {
            subtitle: t('related-projects'),
            item: (
              <BasicInfoContentItem
                wrapperStyle={{ height: '100%' }}
                value={getRelatedProjectNames({
                  project,
                })}
                isLast
              />
            ),
          },
          {
            subtitle: t('overview-and-purpose'),
            item: (
              <Flex vertical>
                <BasicInfoContentItem
                  label={t('project-purpose')}
                  value={project?.purpose}
                />
                <BasicInfoContentItem
                  label={t('project-task')}
                  value={project?.task}
                />
                <BasicInfoContentItem
                  label={t('project-overview')}
                  value={project?.overview}
                />
                <BasicInfoContentItem
                  label={t('overview-url')}
                  value={project?.overviewUrl}
                />

                <BasicInfoContentItem
                  label={t('necessity-of-fund-method')}
                  value={
                    <div style={{ width: '100%' }}>
                      <ReportSectionItemContent
                        wrapperStyle={{ borderBottom: 'none' }}
                        value={
                          <BasicInfoContentItem
                            label={t('fund-necessity-type')}
                            value={
                              project?.fundAdditionalAttributes?.necessityType
                                ? FundNecessityTypeLabelModifier[
                                    project.fundAdditionalAttributes
                                      .necessityType
                                  ]
                                : null
                            }
                          />
                        }
                      />
                      <ReportSectionItemContent
                        wrapperStyle={{ borderBottom: 'none' }}
                        value={
                          <BasicInfoContentItem
                            isLast
                            label={t('fund-necessity-reasons')}
                            value={
                              project?.fundAdditionalAttributes
                                ?.necessityReasons
                            }
                          />
                        }
                      />
                    </div>
                  }
                  isLast
                />
              </Flex>
            ),
          },
          {
            subtitle: t('start-fiscal-year-of-project'),
            item: (
              <BasicInfoContentItem
                wrapperStyle={{ height: '100%' }}
                value={formatProjectStartYearForPdf(project?.startYear)}
                isLast
              />
            ),
          },
          {
            subtitle: t('scheduled-end-time'),
            item: (
              <Flex vertical>
                <NestedContentItem
                  mainRowData={[
                    {
                      label: t('fund-scheduled-end-period'),
                      value:
                        project?.fundAdditionalAttributes?.scheduledEnd
                          ?.endDate,
                    },
                  ]}
                  subRowsData={[
                    {
                      label: t('supplementary-reasons'),
                      value:
                        project?.fundAdditionalAttributes?.scheduledEnd
                          ?.supplementaryReasonsForEndPeriod,
                    },
                    {
                      label: t('reason-for-changing-scheduled-end-period'),
                      value:
                        project?.fundAdditionalAttributes?.scheduledEnd
                          ?.reasonsForChangingScheduledEndPeriod,
                    },
                  ]}
                />
                <NestedContentItem
                  mainRowData={[
                    {
                      label: t('end-date-of-new-application'),
                      value:
                        project?.fundAdditionalAttributes?.scheduledEnd
                          ?.endDateOfNewApplicationReceiving,
                    },
                  ]}
                  subRowsData={[
                    {
                      label: t('supplementary-reasons'),
                      value:
                        project?.fundAdditionalAttributes?.scheduledEnd
                          ?.supplementaryReasonsForNewApplicationReceiving,
                    },
                    {
                      label: t(
                        'reason-for-changing-end-date-of-new-application',
                      ),
                      value:
                        project?.fundAdditionalAttributes?.scheduledEnd
                          ?.reasonsForChangingEndPeriodOfNewApplicationReceiving,
                    },
                  ]}
                  isLast
                />
              </Flex>
            ),
          },
          {
            subtitle: t('legal-bases'),
            item: (
              <ReportSectionItemContent
                isTable
                value={
                  <ReportSectionItemTable
                    header={legalBasesTableData.header}
                    data={legalBasesTableData.rowData}
                  />
                }
              />
            ),
          },
          {
            subtitle: t('plan'),
            item: (
              <ReportSectionItemContent
                isTable
                value={
                  <ReportSectionItemTable
                    header={planTableData.header}
                    data={planTableData.rowData}
                  />
                }
              />
            ),
          },
          {
            subtitle: t('remark'),
            isLongText: true,
            item: <BasicInfoContentItem value={project?.remark} isLast />,
          },
        ]}
      />
    </>
  );
};

const BasicInfoContentItem: FC<{
  label?: string;
  value: ReactNode;
  wrapperStyle?: FlexProps['style'];
  isLast?: boolean;
}> = ({ label, value, isLast, wrapperStyle }) => {
  const basicInfoLabelStyle = {
    width: 150,
    minWidth: 150,
  };
  return (
    <ReportSectionItemContent
      label={label}
      value={value}
      labelStyle={basicInfoLabelStyle}
      wrapperStyle={{
        height: 'auto',
        borderBottom: isLast ? undefined : LEDGER_BORDER,
        ...wrapperStyle,
      }}
    />
  );
};
